import { useCallback, useState } from 'react';
import { post } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { useResource } from '../lib/useResource.js';
import { ErrorBox, Modal, useSubmit, useToast } from './ui.js';

interface Estado {
  ativo: boolean;
  parada: { em: string; por: string | null; motivo: string } | null;
}

/**
 * Botão de emergência do proprietário. Só aparece para quem tem
 * `ai.emergency_stop` — hoje, só o papel Proprietário.
 *
 * Um toque para, com uma confirmação: em emergência não se procura menu. Fica
 * no menu lateral (computador) e na barra de cima (celular), sempre à vista.
 */
export function BotaoDeEmergencia({ compacto = false }: { compacto?: boolean }) {
  const { can } = useSession();
  const pode = can('ai.emergency_stop');
  const [recarga, setRecarga] = useState(0);
  const [aberto, setAberto] = useState(false);
  const toast = useToast();
  const estado = useResource<Estado>(pode ? '/api/ai/emergencia' : null, [recarga], { atualizarACada: 30_000 });

  const parar = useSubmit(
    useCallback(async () => {
      const r = await post<{ respostasCanceladas: number; conversasParaAEquipe: number }>(
        '/api/ai/emergencia/parar',
        { motivo: 'botão de emergência do proprietário' },
      );
      setAberto(false);
      setRecarga((n) => n + 1);
      toast(
        `Agente parado.${r.respostasCanceladas > 0 ? ` ${r.respostasCanceladas} resposta(s) dele canceladas.` : ''}${
          r.conversasParaAEquipe > 0 ? ` ${r.conversasParaAEquipe} conversa(s) foram para a equipe.` : ''
        }`,
      );
    }, [toast]),
  );
  const religar = useSubmit(
    useCallback(async () => {
      await post('/api/ai/emergencia/religar', { motivo: 'religado pelo proprietário' });
      setAberto(false);
      setRecarga((n) => n + 1);
      toast('Agente religado.');
    }, [toast]),
  );

  if (!pode || !estado.data) return null;
  const { ativo, parada } = estado.data;

  const rotulo = ativo ? (compacto ? 'Parar agente' : '⛔ Parar agente') : 'Agente parado';

  return (
    <>
      <button
        type="button"
        className={`emergencia ${ativo ? 'ligado' : 'parado'} ${compacto ? 'compacto' : ''}`}
        onClick={() => setAberto(true)}
        aria-label={ativo ? 'Parar o agente (emergência)' : 'Agente parado — religar'}
      >
        {rotulo}
      </button>

      {aberto ? (
        <Modal
          title={ativo ? 'Parar o agente agora?' : 'Religar o agente?'}
          onClose={() => setAberto(false)}
          footer={
            <>
              <button type="button" onClick={() => setAberto(false)}>
                Cancelar
              </button>
              {ativo ? (
                <button type="button" className="danger" disabled={parar.saving} onClick={() => void parar.submit()}>
                  {parar.saving ? 'Parando…' : 'Parar agora'}
                </button>
              ) : (
                <button type="button" className="primary" disabled={religar.saving} onClick={() => void religar.submit()}>
                  {religar.saving ? 'Religando…' : 'Religar agente'}
                </button>
              )}
            </>
          }
        >
          {ativo ? (
            <div className="stack">
              <p>O agente para de responder <strong>todos os clientes</strong>, na hora.</p>
              <ul>
                <li>Resposta dele que ainda não saiu é cancelada.</li>
                <li>Cliente que estava esperando o agente vai para a equipe, com aviso.</li>
                <li>Toda mensagem nova fica com a equipe.</li>
                <li>Ninguém religa pela configuração: só você, por este botão.</li>
              </ul>
            </div>
          ) : (
            <div className="stack">
              {parada ? (
                <p>
                  Parado em emergência em {new Date(parada.em).toLocaleString('pt-BR')}
                  {parada.por ? ` por ${parada.por}` : ''}.
                </p>
              ) : (
                <p>O agente está desligado.</p>
              )}
              <p>Ao religar, ele volta a responder clientes novos. Conversas que estão com a equipe continuam com a equipe.</p>
            </div>
          )}
          <ErrorBox error={parar.error ?? religar.error} />
        </Modal>
      ) : null}
    </>
  );
}
